import React, { useState, useRef } from 'react';
import { uploadPDF } from './vectorStore';
import { IoDocumentAttach, IoCloudUpload, IoCheckmarkCircle, IoAlertCircle } from 'react-icons/io5';
import './PDFUploader.css';

interface PDFUploaderProps {
  onUploadComplete?: () => void;
}

const PDFUploader: React.FC<PDFUploaderProps> = ({ onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'idle' | 'uploading' | 'success' | 'error';
    message: string;
  }>({ type: 'idle', message: '' });
  const [fileName, setFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CORRECT_PASSWORD = '1590404';

  const verifyPassword = () => {
    if (password === CORRECT_PASSWORD) {
      setIsPasswordCorrect(true);
      setUploadStatus({
        type: 'success',
        message: 'Password verified. You can now upload files.'
      });
    } else {
      setIsPasswordCorrect(false);
      setUploadStatus({
        type: 'error',
        message: 'Incorrect password. Please try again.'
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      
      if (!isPasswordCorrect) {
        setShowPasswordInput(true);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      
      if (!isPasswordCorrect) {
        setShowPasswordInput(true);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUpload = async () => {
    if (!file || !isPasswordCorrect) return;
    
    setIsUploading(true);
    setUploadStatus({
      type: 'uploading',
      message: 'Uploading and processing PDF... (Extracting text and analyzing visuals)'
    });

    try {
      console.log(`Starting to process ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      const success = await uploadPDF(file);
      
      if (success) {
        setUploadStatus({
          type: 'success',
          message: `Successfully processed ${file.name}! The chatbot will now use this knowledge, including any charts or figures.`
        });
        setFile(null);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        setUploadStatus({
          type: 'error',
          message: 'Error processing PDF. Please try again with a different file.'
        });
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      setUploadStatus({
        type: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus.type) {
      case 'uploading':
        return <div className="spinner"></div>;
      case 'success':
        return <IoCheckmarkCircle className="status-icon success" />;
      case 'error':
        return <IoAlertCircle className="status-icon error" />;
      default:
        return <IoCloudUpload className="upload-icon" />;
    }
  };

  return (
    <div className="pdf-uploader">
      <h3>Upload Knowledge</h3>
      <p>Upload PDF documents to enhance the chatbot's knowledge base.</p>
      
      <div 
        className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClickUpload}
      >
        <input 
          ref={fileInputRef}
          type="file" 
          accept="application/pdf" 
          onChange={handleFileChange}
          className="file-input"
        />
        
        <div className="upload-content">
          {getStatusIcon()}
          
          <div className="upload-text">
            {uploadStatus.type === 'idle' ? (
              <>
                <span className="primary-text">Drop PDF here or click to browse</span>
                <span className="secondary-text">Max file size: 10MB</span>
              </>
            ) : (
              <span className={`status-message ${uploadStatus.type}`}>
                {uploadStatus.message}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="recent-uploads">
        <h4>Recent Uploads</h4>
        {fileName ? (
          <div className="upload-item">
            <IoDocumentAttach className="doc-icon" />
            <span className="filename">{fileName}</span>
          </div>
        ) : (
          <p className="no-uploads">No documents uploaded yet.</p>
        )}
      </div>
      
      {showPasswordInput && !isPasswordCorrect && (
        <div className="password-section">
          <p>Please enter the password to upload documents:</p>
          <div className="password-input-container">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="password-input"
            />
            <button 
              onClick={verifyPassword}
              className="verify-button"
              disabled={!password}
            >
              Verify
            </button>
          </div>
        </div>
      )}
      
      {file && isPasswordCorrect && (
        <button 
          onClick={handleUpload} 
          disabled={isUploading || !file || !isPasswordCorrect}
          className="upload-button"
        >
          {isUploading ? 'Processing...' : 'Upload PDF'}
        </button>
      )}
    </div>
  );
};

export default PDFUploader; 